/**
 * Human-readable log messages for the add-to-cart diagnostics.
 *
 * The message is what a person sees in the Datadog log list, so it states what
 * happened and the numbers that matter. Everything that is queried or
 * aggregated stays in the structured fields; the messages are only built from
 * them.
 *
 * Datadog's `beforeSend` filter drops browser logs whose message contains
 * "token" or "password", so messages never use those words.
 */

import type { CartFlowFields } from "./cart-flow-telemetry";

const CATEGORY_TEXT: Record<string, string> = {
  invalid_request: "the request was invalid",
  context_network: "the cart page request failed",
  context_timeout: "the cart page request timed out",
  context_invalid_response: "the cart page could not be read",
  context_http_error: "the cart page request failed",
  context_no_csrf: "the cart page has no CSRF value",
  context_flag_ambiguous: "the delivery-type flag is missing or conflicting",
  post_network: "the add request lost its connection",
  post_timeout: "the add request timed out",
  post_http_error: "the add request answered with an error status",
  post_bad_payload: "the answer to the add request could not be read",
  post_accelerator_error: "the shop rejected the item",
  unexpected_error: "an unexpected error occurred",
};

const UI_MESSAGE_TEXT: Record<string, string> = {
  fallback: "the generic error message",
  unconfirmed: "the 'cart update could not be confirmed' message",
  accelerator: "the error message of the shop",
};

const TRIGGER_TEXT: Record<string, string> = {
  mount: "on page load",
  cartUpdated: "after a cart change",
  focus: "when the tab regained focus",
  pageshow: "when the page was restored",
  otherTab: "after a cart change in another tab",
};

/**
 * Every message starts with this, so one Datadog search for `Add to cart:`
 * finds the whole chain: click, cart page, add request, result and the
 * mini-cart refresh.
 */
export const MESSAGE_PREFIX = "Add to cart: ";

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60_000;

const SENSITIVE_WORD_PATTERN = /token|password/i;

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** "HTTP 200" or "HTTP 901 (non-standard status)". */
function status(fields: CartFlowFields): string {
  const code = fields.httpStatus;

  if (typeof code !== "number") {
    return "no status";
  }

  return fields.statusClass === "non_standard"
    ? `HTTP ${code} (non-standard status)`
    : `HTTP ${code}`;
}

/** " in 312 ms" for a request that got an answer. */
function took(fields: CartFlowFields, key = "durationMs"): string {
  const value = fields[key];

  return typeof value === "number" ? ` in ${value} ms` : "";
}

/** " after 12 ms" for something that did not end well. */
function after(fields: CartFlowFields, key = "durationMs"): string {
  const value = fields[key];

  return typeof value === "number" ? ` after ${value} ms` : "";
}

/**
 * A request sent before this tab had any answer from the shop has no session
 * yet; a failure like that reads differently from one in a running session.
 */
function firstContact(fields: CartFlowFields): string {
  return fields.acceleratorContactBefore === "none"
    ? " (first request to the shop in this tab)"
    : "";
}

function errorDescription(fields: CartFlowFields): string {
  const name = text(fields.errorName) ?? "unknown error";
  // The browser's own wording, e.g. "TypeError: Failed to fetch".
  const detail = text(fields.errorText);
  const label = detail ? `${name}: ${detail}` : name;

  if (fields.errorKind === "timeout") {
    return `timeout (${label})`;
  }

  return fields.errorKind === "network"
    ? `network error (${label})`
    : `error (${label})`;
}

/** "800 ms", "45 s" or "3 min". */
function elapsed(ms: number): string {
  if (ms < MS_PER_SECOND) {
    return `${ms} ms`;
  }

  return ms < MS_PER_MINUTE
    ? `${Math.round(ms / MS_PER_SECOND)} s`
    : `${Math.round(ms / MS_PER_MINUTE)} min`;
}

function cartPageMessage(fields: CartFlowFields): string {
  if (fields.noResponse === true) {
    return `Cart page request failed without a response: ${errorDescription(fields)}${after(fields)}${firstContact(fields)}`;
  }

  if (fields.noResponse === false && text(fields.errorName)) {
    return `Cart page could not be read: ${text(fields.errorName)}`;
  }

  if (fields.statusClass !== "2xx") {
    const target =
      fields.redirected === true ? text(fields.finalPath) : undefined;
    const redirect = target ? `, redirected to ${target}` : "";

    return `Cart page request failed: ${status(fields)}${redirect}${took(fields)}${firstContact(fields)}`;
  }

  if (fields.csrfSource === "none") {
    return `Cart page loaded (${status(fields)}) but has no CSRF value`;
  }

  if (fields.deliveryFlag === "missing" || fields.deliveryFlag === "conflict") {
    return `Cart page loaded (${status(fields)}) but the delivery-type flag is ${fields.deliveryFlag}`;
  }

  return `Cart page loaded: ${status(fields)}${took(fields)}`;
}

function addRequestMessage(fields: CartFlowFields): string {
  if (fields.noResponse === true) {
    return `Add request failed without a response: ${errorDescription(fields)}${after(fields)}`;
  }

  if (fields.statusClass !== "2xx") {
    return `Add request failed: ${status(fields)}${took(fields)}`;
  }

  if (fields.hasErrorMsg === true) {
    return `Add request rejected by the shop: ${status(fields)}${took(fields)}`;
  }

  if (fields.payloadShape !== "valid") {
    return `Add request answered with an unreadable body (${text(fields.payloadShape)}): ${status(fields)}${took(fields)}`;
  }

  return `Add request confirmed: ${status(fields)}${took(fields)}`;
}

function resultMessage(fields: CartFlowFields): string {
  if (fields.outcome === "success") {
    return `succeeded${took(fields, "totalDurationMs")}`;
  }

  const category = text(fields.errorCategory) ?? "unexpected_error";
  const reason = CATEGORY_TEXT[category] ?? category;
  const http =
    typeof fields.httpStatus === "number" && fields.statusClass !== "2xx"
      ? `, ${status(fields)}`
      : "";
  let consequence = "; no add request was sent";

  if (fields.postAttempted === true) {
    consequence =
      fields.outcomeKnown === true
        ? ""
        : "; the cart may or may not have changed";
  }

  const verb = fields.outcome === "unknown" ? "outcome unknown" : "failed";

  return `${verb}${after(fields, "totalDurationMs")}: ${reason}${http}${consequence}${firstContact(fields)}`;
}

/**
 * What helps to judge a failed mini-cart refresh: what made it run, whether it
 * is a one-off or a run of failures, when it last worked, and the
 * circumstances. Returned as " (a, b, c)", or "" when there is nothing to say.
 */
function cartCountContext(fields: CartFlowFields): string {
  const parts: string[] = [];
  const trigger = text(fields.trigger);

  if (trigger) {
    parts.push(TRIGGER_TEXT[trigger] ?? trigger);
  }

  if (typeof fields.failuresInARow === "number" && fields.failuresInARow > 1) {
    parts.push(`failure ${fields.failuresInARow} in a row`);
  }

  if (typeof fields.lastSuccessAgoMs === "number") {
    parts.push(`last success ${elapsed(fields.lastSuccessAgoMs)} ago`);
  } else if (fields.successesThisPage === 0) {
    parts.push("no success on this page yet");
  }

  if (text(fields.overlapsAttemptId)) {
    parts.push("while an add to cart was in progress");
  }

  if (fields.online === false) {
    parts.push("browser is offline");
  }

  if (fields.acceleratorContactBefore === "none") {
    parts.push("first request to the shop in this tab");
  }

  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

const PAYLOAD_PROBLEM_TEXT: Record<string, string> = {
  not_json: "the answer is not JSON",
  not_an_object: "the answer is not an object",
  no_count: "the answer has no count",
};

function payloadProblem(fields: CartFlowFields): string {
  const problem = text(fields.payloadProblem);

  if (problem === "invalid_count") {
    const value =
      typeof fields.countValue === "number" ? ` ${fields.countValue}` : "";

    return `, the count is a ${text(fields.countType) ?? "value"}${value} and not a whole number`;
  }

  return problem ? `, ${PAYLOAD_PROBLEM_TEXT[problem] ?? problem}` : "";
}

function cartCountMessage(fields: CartFlowFields): string {
  const context = cartCountContext(fields);

  if (fields.errorCategory === "http_error") {
    return `Mini-cart refresh failed: ${status(fields)}${took(fields)}${context}`;
  }

  if (fields.errorCategory === "bad_payload") {
    return `Mini-cart refresh got an unexpected answer: ${status(fields)}${payloadProblem(fields)}${context}`;
  }

  return `Mini-cart refresh failed without a response: ${errorDescription(fields)}${after(fields)}${context}`;
}

/**
 * The message for one event, e.g. `add_to_cart.context_fetch` →
 * "Add to cart: Cart page request failed: HTTP 901 (non-standard status) in 120 ms".
 * Falls back to the event name for unknown events and for any text that would
 * be dropped by the Datadog log filter.
 */
export function readableMessage(event: string, fields: CartFlowFields): string {
  let message = event;

  switch (event) {
    case "add_to_cart.click":
      message = `clicked (click ${fields.clickSeq}, ${fields.locale}, product ${fields.productCode})`;
      break;
    case "add_to_cart.context_fetch":
      message = cartPageMessage(fields);
      break;
    case "add_to_cart.post":
    case "add_to_cart.post_response":
      message = addRequestMessage(fields);
      break;
    case "add_to_cart.result":
      message = resultMessage(fields);
      break;
    case "add_to_cart.ui_error_shown":
      message = `Shown to the user: ${UI_MESSAGE_TEXT[String(fields.messageKind)] ?? "an error message"}`;
      break;
    case "cart_count.fetch":
      message = cartCountMessage(fields);
      break;
    default:
      break;
  }

  return `${MESSAGE_PREFIX}${SENSITIVE_WORD_PATTERN.test(message) ? event : message}`;
}
